import { useRef } from 'react';
import { MTGCard } from 'types/interfaces/mtgCard';
import { ConsolidatedCard } from 'store/services/cards/search/cardsSearchApi';
import { SetIcon } from 'components/SetIcon';
import {
  cardImageInputsFromCard,
  getCardImageUrl,
  makeCardImageErrorHandler,
} from 'utils/cardImageFallback';
import './AddCardsSearchListItem.css';

export interface HoverPreview {
  scryfallId?: string;
  imageUrl?: string;
  normalFallbacks?: string[];
  cardName: string;
}

interface Props {
  item: MTGCard | ConsolidatedCard;
  additionalStyle: string;
  onPress: () => void;
  mode?: 'any' | 'specific';
  /** What a click on this printing will add, e.g. "Add as LP · Foil". */
  previewLine?: string;
  onHover?: (preview: HoverPreview) => void;
  onHoverEnd?: () => void;
}

export const AddCardsSearchListItem = ({
  item,
  additionalStyle,
  onPress,
  mode = 'specific',
  previewLine,
  onHover,
  onHoverEnd
}: Props) => {
  // Fallback step per element lives in a ref, never in DOM dataset (re-renders reset it).
  const fallbackStepRef = useRef<Map<string | number, number>>(new Map());
  // Type guard to check if item is ConsolidatedCard
  const isConsolidated = (item: any): item is ConsolidatedCard => {
    return 'cardName' in item && 'totalVersions' in item;
  };

  if (mode === 'any' && isConsolidated(item)) {
    // Display for "Any Set" mode (consolidated results)
    return (
      <div
        className={`add-cards-search-list-item ${additionalStyle}`}
        onClick={onPress}
        onMouseEnter={() =>
          onHover?.({
            imageUrl: item.sampleImage?.normal || item.sampleImage?.small,
            cardName: item.cardName
          })
        }
        onMouseLeave={onHoverEnd}
      >
        <img
          src={item.sampleImage?.art_crop || item.sampleImage?.small}
          alt={item.cardName + ' image'}
        />
        <div className="add-cards-search-list-item-name">{item.cardName}</div>
        {/* No set information for "Any Set" mode */}
      </div>
    );
  } else {
    // Display for "Specific Set" mode (MTG card results)
    const mtgCard = item as MTGCard;
    // One resolver for every surface: handles DFCs (images only on cardFaces[0]),
    // the lowercase `scryfallId` the search endpoints send, and the CDN-by-id path.
    const imageInputs = cardImageInputsFromCard(mtgCard);
    const scryfallId = imageInputs.scryfallId ?? undefined;
    // Handle both camelCase and snake_case from backend
    const collectorNumber = mtgCard.collectorNumber || (mtgCard as any).collector_number;
    // Reuse the shared set-symbol assets (individual SVGs from S3), same URL pattern as CardTradeTile
    const setIconUrl = mtgCard.setCode
      ? `https://trademagic-assets.s3.us-west-1.amazonaws.com/sets/icons/${mtgCard.setCode.toLowerCase()}.svg`
      : null;
    return (
      <div
        className={`add-cards-search-list-item add-cards-search-list-item--card ${additionalStyle}`}
        onClick={onPress}
        onMouseEnter={() =>
          onHover?.({
            scryfallId,
            imageUrl: getCardImageUrl(imageInputs, 'normal'),
            normalFallbacks: mtgCard.normal_fallbacks,
            cardName: mtgCard.name
          })
        }
        onMouseLeave={onHoverEnd}
      >
        <img
          className="add-cards-search-list-item-card"
          src={getCardImageUrl(imageInputs, 'small')}
          alt={mtgCard.name + ' image'}
          width={48}
          height={67}
          loading="lazy"
          onError={makeCardImageErrorHandler(
            imageInputs,
            fallbackStepRef,
            scryfallId ?? mtgCard.id,
            'small',
          )}
        />
        <div className="add-cards-search-list-item-name">
          {mtgCard.name}
          {previewLine && (
            <div className={`add-cards-search-list-item-preview${previewLine.startsWith('No ') ? ' add-cards-search-list-item-preview--adjusted' : ''}`}>
              {previewLine}
            </div>
          )}
        </div>
        <div className="add-cards-search-list-item-set-container">
          <SetIcon
            setIconUrl={setIconUrl}
            setName={mtgCard.setName}
            size="sm"
            lazy
          />
          <div className="add-cards-search-list-item-set-name">
            {mtgCard.setName}
            {mtgCard.setCode && (
              <span className="add-cards-search-list-item-set-code"> {mtgCard.setCode.toUpperCase()}</span>
            )}
            {collectorNumber && (
              <span className="add-cards-search-list-item-collector-number">
                #{collectorNumber}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }
};
